<script setup lang="ts">
import type { Announcement } from '@/api/announcement/announcementType';
import type { Subject as ApiSubject } from '@/api/do-question/doQuestion.ts';
import type { Subject } from '@/components/subject/configs.ts';
import { vAutoAnimate } from '@formkit/auto-animate';
import { BookOpen } from 'lucide-vue-next';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { dismissAnnouncementApi, fetchCurrentAnnouncementApi } from '@/api/announcement/announcementApi';
import { reviewOfficialCourseApi } from '@/api/course/courseApi';
import { fetchSubjectListApi } from '@/api/do-question/doQuestion.ts';
import { fetchFavoriteExperimentsApi } from '@/api/experiment/experimentApi.ts';
import { completeProfileApi, fetchUserApi } from '@/api/user/userApi';
import AnnouncementPopup from '@/components/announcement/AnnouncementPopup.vue';
import { getCourseColor, restoreDynamicCourseContext } from '@/components/course/courseUtils';
import HomeLanding from '@/components/home/HomeLanding.vue';
import NavBar from '@/components/navbar/NavBar.vue';
import SideBar from '@/components/sidebar/SideBar.vue';
import StudyCoachFloating from '@/components/study-coach/StudyCoachFloating.vue';
import { experimentConfigs, subjectConfig } from '@/components/subject/configs.ts';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useConfigStore, useUserStore } from '@/store';
import { isPlatformAdminUser, isVisitor } from '@/utils/auth.ts';
import { error, success } from '@/utils/toast';
import { buildPageShellClass } from './pageShellClass';

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const pageShellClass = buildPageShellClass();
const currentAnnouncement = ref<Announcement | null>(null);

async function loadCurrentAnnouncement() {
  if (!localStorage.getItem('token') && !isLocalPreviewSession())
    return;
  try {
    const result = await fetchCurrentAnnouncementApi();
    currentAnnouncement.value = result.announcement;
  }
  catch {
    // 公告接口不可用时不影响首页主体。
  }
}

function isLocalPreviewSession() {
  return import.meta.env.DEV
    && (
      sessionStorage.getItem('probfun_local_teacher_preview') === '1'
      || sessionStorage.getItem('probfun_local_admin_preview') === '1'
      || sessionStorage.getItem('probfun_local_student_preview') === '1'
    );
}

onMounted(async () => {
  // Fetch the homepage announcement independently; profile and course requests
  // can be slow and should not delay the popup.
  void loadCurrentAnnouncement();

  if (isVisitor()) {
    return;
  }
  if (!localStorage.getItem('token') && userStore.user && !isLocalPreviewSession()) {
    return;
  }

  // 1. 如果 Store 里已经有用户信息了（刚从登录页过来），就不用急着重新请求
  if (userStore.user && userStore.user.id) {
    // 可以在这里静默刷新，或者直接跳过
    // 如果一定要刷新，往下走，但要把 catch 里的踢人逻辑去掉
  }

  if (!isLocalPreviewSession()) {
    try {
      const result = await fetchUserApi();
      userStore.user = result.user;
    }
    catch (error: any) {
      console.error('Failed to fetch user info:', error);

      // 开发环境预览允许无登录查看课程；生产环境仍按原逻辑回登录页。
      if (error?.response?.status === 401 && !import.meta.env.DEV) {
        await router.push('/login');
      }
    }
  }

  if (userStore.user?.requiresProfileSetup)
    return;

  try {
    const response = await fetchFavoriteExperimentsApi();
    userStore.favoriteExperiments = response.experiments;
  }
  catch (error) {
    console.error('获取收藏实验失败:', error);
  }

  await loadApprovedPublicSubjects();
});

async function closeAnnouncement() {
  const announcement = currentAnnouncement.value;
  currentAnnouncement.value = null;
  if (!announcement)
    return;
  try {
    await dismissAnnouncementApi(announcement.id);
  }
  catch {
    // 关闭失败不阻塞用户继续使用首页。
  }
}

// 微信新用户资料补充弹窗
const showSetup = computed(() => Boolean(userStore.user?.requiresProfileSetup));
const isBuptAuthenticated = computed(() => Boolean(userStore.user?.buptAuthenticated));
const setupForm = reactive({ realname: '', college: '', grade: '', studentId: '', major: '', phone: '' });
const setupSaving = ref(false);

watch(() => userStore.user, (user) => {
  if (!user)
    return;
  Object.assign(setupForm, {
    realname: user.realName === '微信用户' ? '' : user.realName || '',
    college: user.college || '',
    grade: String(user.grade || ''),
    studentId: user.studentId || '',
    major: user.major || '',
    phone: user.phone || sessionStorage.getItem('setup_phone') || '',
  });
}, { immediate: true });

function startSetupBuptBinding() {
  sessionStorage.setItem('setup_phone', setupForm.phone.trim());
  sessionStorage.setItem('casAuthMode', 'bind-bupt');
  const serviceUrl = encodeURIComponent('https://umathhub.com/callback');
  window.location.href = `https://auth.bupt.edu.cn/authserver/login?service=${serviceUrl}`;
}

async function submitProfile() {
  if (!/^1[3-9]\d{9}$/.test(setupForm.phone.trim())) {
    error('请输入正确的11位电话号码');
    return;
  }
  if (!isBuptAuthenticated.value && [setupForm.realname, setupForm.college, setupForm.grade, setupForm.major, setupForm.studentId].some(value => !value.trim())) {
    error('请完整填写姓名、学校、年级、专业和学号');
    return;
  }
  setupSaving.value = true;
  try {
    const result = await completeProfileApi({
      phone: setupForm.phone.trim(),
      ...(!isBuptAuthenticated.value && {
        realName: setupForm.realname.trim(),
        college: setupForm.college.trim(),
        grade: setupForm.grade.trim(),
        major: setupForm.major.trim(),
        studentId: setupForm.studentId.trim(),
      }),
    });
    userStore.user = result.user;
    sessionStorage.removeItem('setup_phone');
    const redirect = sessionStorage.getItem('post_login_redirect');
    sessionStorage.removeItem('post_login_redirect');
    router.replace(redirect || '/');
    success('资料已保存，欢迎加入！');
    await loadApprovedPublicSubjects();
  }
  catch (e: any) {
    const message = e?.response?.data?.message;
    error(typeof message === 'object' && message
      ? Object.values(message).flat().join('；')
      : message || e?.message || '保存失败');
  }
  finally { setupSaving.value = false; }
}

const configStore = useConfigStore();
const restoredDynamicSubject = restoreDynamicCourseContext();
if (restoredDynamicSubject && !configStore.currentSubjectId && !configStore.dynamicSubject)
  configStore.dynamicSubject = restoredDynamicSubject;

const standaloneRoutePaths = ['/membership'];
const isStandaloneRoute = computed(() => standaloneRoutePaths.includes(route.path));
type SubjectId = keyof typeof subjectConfig;
interface DrawerRouteItem {
  route?: string;
  children?: DrawerRouteItem[];
}

const defaultSubjectId: SubjectId = 'probability';
const probabilityRoutePaths = new Set([
  '/analysis',
  '/mindmap/distribution',
  '/mindmap/chapter-1',
  '/mindmap/chapter-4',
]);

function drawerItemsContainRoute(items: DrawerRouteItem[] | undefined, path: string): boolean {
  return items?.some(item =>
    item.route === path || drawerItemsContainRoute(item.children, path),
  ) ?? false;
}

function resolveSubjectIdForRoute(path: string): SubjectId | null {
  for (const [subjectId, config] of Object.entries(experimentConfigs) as Array<[SubjectId, { experiments: DrawerRouteItem[] }]>) {
    if (drawerItemsContainRoute(config.experiments, path)) {
      return subjectId;
    }
  }

  if (
    probabilityRoutePaths.has(path)
    || path.startsWith('/question/')
    || path.startsWith('/mindmap/question/')
  ) {
    return defaultSubjectId;
  }

  return null;
}

watch(
  () => route.path,
  (path) => {
    if (configStore.currentSubject)
      return;

    const subjectId = resolveSubjectIdForRoute(path);
    if (subjectId) {
      configStore.currentSubjectId = subjectId;
    }
  },
  { immediate: true },
);
const shouldShowRouteContent = computed(() => route.path !== '/' || Boolean(configStore.currentSubject));

const universitySubjectIds = [
  'advanced-math-1',
  'advanced-math-2',
  'linear-algebra',
  'linear-algebra-en',
  'probability',
  'probability-bilingual',
  'number-theory',
  'statistics',
  'risk-theory',
] as const;

const publicSubjects = ref<Subject[]>([]);
const delistOpen = ref(false);
const delistReason = ref('');
const delistTarget = ref<Subject | null>(null);
const delisting = ref(false);
const platformSubjectBackendIds = new Set(Object.values(subjectConfig).map(subject => String(subject.id_)));
const universityBaseSubjects = computed(() => universitySubjectIds.map(id => subjectConfig[id]));
const universitySubjects = computed(() => [...universityBaseSubjects.value, ...publicSubjects.value]);
const manageablePublicSubjectIds = computed(() =>
  isPlatformAdminUser(userStore.user)
    ? publicSubjects.value.map(subject => String(subject.id))
    : [],
);

function toPublicSubject(subject: ApiSubject, index: number): Subject {
  return {
    id: `public-course-${subject.id}`,
    id_: String(subject.id),
    name: subject.name,
    subjectName: subject.name,
    description: subject.description || '公开课程',
    color: getCourseColor(index),
    teacher: subject.teacher_name || '课程作者',
    icon: BookOpen,
    isCustomCourse: true,
  } as unknown as Subject;
}

async function loadApprovedPublicSubjects() {
  try {
    const result = await fetchSubjectListApi();
    publicSubjects.value = (result.subjects ?? [])
      .filter(subject =>
        subject.is_public_course === true
        && subject.official_review_status === 'approved'
        && !platformSubjectBackendIds.has(String(subject.id)),
      )
      .map((subject, index) => toPublicSubject(subject, universityBaseSubjects.value.length + index));
  }
  catch (err) {
    console.error('获取公共课程失败:', err);
  }
}

function openDelistPublicSubject(subject: Subject) {
  delistTarget.value = subject;
  delistReason.value = '';
  delistOpen.value = true;
}

async function submitDelistPublicSubject() {
  const subject = delistTarget.value;
  const reason = delistReason.value.trim();
  const courseId = Number((subject as any)?.id_);
  if (!subject || !Number.isFinite(courseId) || courseId <= 0) {
    error('课程信息异常，无法下架');
    return;
  }
  if (!reason) {
    error('请填写下架原因');
    return;
  }

  delisting.value = true;
  try {
    await reviewOfficialCourseApi(courseId, { action: 'delist', reason });
    publicSubjects.value = publicSubjects.value.filter(item => item.id !== subject.id);
    delistOpen.value = false;
    success('课程已下架');
  }
  catch (err: any) {
    error(err?.response?.data?.message || '下架失败');
  }
  finally {
    delisting.value = false;
  }
}
</script>

<template>
  <div v-auto-animate :class="!showSetup && !shouldShowRouteContent ? 'home-shell' : pageShellClass">
    <NavBar v-if="!showSetup && shouldShowRouteContent" />
    <router-view v-if="!showSetup && isStandaloneRoute" class="flex-1 overflow-y-auto" />
    <div v-else-if="!showSetup && shouldShowRouteContent" v-auto-animate class="flex-1 gap-2 flex relative transition-all overflow-hidden">
      <SideBar v-if="configStore.currentSubject" />
      <router-view class="flex-1 overflow-y-auto" />
    </div>
    <HomeLanding
      v-else-if="!showSetup"
      :subjects="universitySubjects"
      :manageable-subject-ids="manageablePublicSubjectIds"
      @manage-subject="openDelistPublicSubject"
    />

    <AnnouncementPopup
      v-if="!showSetup && !configStore.currentSubject && route.path === '/' && currentAnnouncement"
      :announcement="currentAnnouncement"
      @close="closeAnnouncement"
    />

    <StudyCoachFloating v-if="!showSetup && configStore.currentSubject && shouldShowRouteContent && !isStandaloneRoute" />

    <div v-if="showSetup" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div role="dialog" aria-modal="true" aria-label="完善你的资料" class="w-full max-w-sm max-h-[90vh] overflow-y-auto bg-card rounded-2xl shadow-2xl p-8 mx-4 space-y-5">
        <h2 class="text-xl font-bold text-center">
          完善你的资料
        </h2>
        <p class="text-sm text-muted-foreground text-center">
          {{ isBuptAuthenticated ? '北邮统一认证已完成，请补充电话号码后继续使用' : '请补全姓名、学校、年级、专业、学号和电话号码后继续使用。北邮学生可通过下方统一认证，仅需补充电话号码。完成前无法关闭此页面。' }}
        </p>
        <div class="space-y-3">
          <template v-if="!isBuptAuthenticated">
            <Input v-model="setupForm.realname" aria-label="姓名" placeholder="姓名" maxlength="50" :disabled="setupSaving" />
            <Input v-model="setupForm.college" aria-label="学校" placeholder="学校" maxlength="100" :disabled="setupSaving" />
            <Input v-model="setupForm.grade" aria-label="年级" placeholder="年级（如2026级）" maxlength="20" :disabled="setupSaving" />
            <Input v-model="setupForm.major" aria-label="专业" placeholder="专业" maxlength="100" :disabled="setupSaving" />
            <Input v-model="setupForm.studentId" aria-label="学号" placeholder="学号" maxlength="50" :disabled="setupSaving" />
          </template>
          <Input v-model="setupForm.phone" aria-label="电话号码" type="tel" placeholder="电话号码" maxlength="11" :disabled="setupSaving || !!userStore.user?.phone" />
        </div>
        <div class="flex gap-3">
          <Button class="flex-1" :disabled="setupSaving" @click="submitProfile">
            保存
          </Button>
        </div>
        <Button v-if="!isBuptAuthenticated" variant="outline" class="w-full" :disabled="setupSaving" @click="startSetupBuptBinding">
          北邮学生统一认证
        </Button>
      </div>
    </div>

    <Dialog v-model:open="delistOpen">
      <DialogContent class="max-w-lg">
        <DialogHeader>
          <DialogTitle>下架课程</DialogTitle>
        </DialogHeader>
        <Textarea
          v-model="delistReason"
          class="min-h-32 resize-none"
          placeholder="填写下架原因"
          :disabled="delisting"
        />
        <DialogFooter>
          <Button variant="outline" :disabled="delisting" @click="delistOpen = false">
            取消
          </Button>
          <Button variant="destructive" :disabled="delisting || !delistReason.trim()" @click="submitDelistPublicSubject">
            {{ delisting ? '下架中' : '确认下架' }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<style scoped>
.home-shell {
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
</style>
